// New version src/Common/server_date/server_date.service.ts CommonServerDateService
// Old version src/CaseDotStar.ServicePackages.Frontend.Common/scripts/common/services/server_date_service_factory.ts ServerDateService
import { Injectable } from '@angular/core';

import {
	extend,
	find,
	isArray,
	isEmpty,
	isNull,
	isString,
	map,
	some,
} from 'lodash';
import * as moment from 'moment';

import {
	Int,
	UUID,
} from '../@types/core';
import { CommonLocaleService } from './locale/locale.service';
import { CommonGeneralSettingsService } from './general_settings.service';

// Helper for generating Opaque types.
type Opaque<T, K> = T & { __opaque__: K };
type TCommonServerDateFormatString = Opaque<string, 'TCommonServerDateFormatString'>; // 'YYYY-MM-DD'
type TCommonServerNonWorkingRule = (day: moment.Moment) => boolean;

type TCommonServerDateMomentPeriod = (
	'year' | 'years' | 'y' |
	'month' | 'months' | 'M' |
	'week' | 'weeks' | 'w' |
	'day' | 'days' | 'd' |
	'hour' | 'hours' | 'h' |
	'minute' | 'minutes' | 'm' |
	'second' | 'seconds' | 's' |
	'millisecond' | 'milliseconds' | 'ms'
);

type TCommonServerDateMomentPeriodQuarter = 'quarter' | 'quarters' | 'Q';

interface ICommonServerDateWeekDay {
	Id: UUID;
	Name: string;
	Order: Int;
	IndexNumber: Int;
	SysName: string;
}

interface ICommonServerDateNonWorkingDay {
	Name?: string;
	StartDate: TCommonServerDateFormatString;
	EndDate: TCommonServerDateFormatString;
}

export const DAYS_SYMBOL = 'dd';
export const MONTHS_SYMBOL = 'mm';
export const YEARS_SYMBOL = 'yyyy';
export const DAYS_IN_WEEK = 7;

const DEFAULT_SETTINGS = {
	firstDay: 0,
};

@Injectable()
export class CommonServerDateService {
	daysSymbol = 'dd';
	monthsSymbol = 'mm';
	yearsSymbol = 'yyyy';
	secondsInMinute = 60;
	minutesInHour = 60;
	hoursInDay = 24;
	secondsInDay = 24 * 60 * 60;
	daysInWeek = 7;
	datetimeFormat = 'YYYY-MM-DD[T]HH:mm:ss.SSSZ';
	localDateTimeFormat = 'YYYY-MM-DD[T]HH:mm:ss';
	dateFormat = 'YYYY-MM-DD';
	workingDays: ICommonServerDateWeekDay[] = [];
	nonWorkingDays: ICommonServerDateNonWorkingDay[] = [];
	shortDateFormat = 'DD.MM.Y';
	inputDateFormat = this.shortDateFormat;
	mediumDateFormat = 'D MMM Y';
	longDateFormat = 'D MMMM Y';
	displayTimeFormat = 'HH:mm';
	maskDelimiter = '.';
	shortWithTime = `${this.shortDateFormat}, ${this.displayTimeFormat}`;
	mediumWithTime = `${this.mediumDateFormat}, ${this.displayTimeFormat}`;
	timeWithMedium = `${this.displayTimeFormat}, ${this.mediumDateFormat}`;
	dateTestRegex: RegExp;
	inputDateTestRegex: RegExp;
	inputDateMask = '';
	inputDatePlaceholder = '';
	inputTimeMask = '';
	inputTimePlaceholder = '';
	inputFormatWithFirstZero = -1 < this.inputDateFormat.indexOf('DD');
	amDesignator = '';
	pmDesignator = '';
	firstDay = 0;

	private inputTimeRegex = /\d{2}:\d{2}/;
	private isoDateTestRegex = /\d{4}-\d{2}-\d{2}/;

	private dateTestRegexByYear = {
		yyyymmdd: /\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2}/,
		mmddyyyy: /\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{4}/,
		ddmmyyyy: /\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{4}/,
	};

	private inputDateTestRegexByYear = {
		yyyymmdd: /\d{4}[\/\-.]\d{2}[\/\-.]\d{2}/,
		mmddyyyy: /\d{2}[\/\-.]\d{2}[\/\-.]\d{4}/,
		ddmmyyyy: /\d{2}[\/\-.]\d{2}[\/\-.]\d{4}/,
	};

	private TIME_KEYS_FOR_DST_FIX = ['days', 'day', 'weeks', 'week', 'months', 'month', 'quarters', 'quarter', 'years', 'year', 'd', 'w', 'M', 'Q', 'y'];
	private workingDayIndexes: Int[];
	private nonWorkingDaysRules: TCommonServerNonWorkingRule[];

	constructor (
		private commonLocaleService: CommonLocaleService,
		private commonGeneralSettingsService: CommonGeneralSettingsService,
	) {}

	init () {
		this.workingDays = this.commonGeneralSettingsService.getGeneralSettings('WorkingDays');
		this.nonWorkingDays = this.commonGeneralSettingsService.getGeneralSettings('NonWorkingDays');
		this.extendDateFormatsFromGeneralSettings();

		this.setInputSettings();

		this.workingDayIndexes = map(this.workingDays, (day) => {
			const index = day.IndexNumber + 1;

			if (index === DAYS_IN_WEEK) {
				return 0;
			} else {
				return index;
			}
		}) as Int[];

		this.nonWorkingDaysRules = this.createNonWorkingRules(this.nonWorkingDays);
	}

	getMoment (dateString?: string | moment.Moment | object | number): moment.Moment {
		const invalidDateString = dateString === 'current' || isNull(dateString) || dateString === '' || dateString === undefined;
		return moment( invalidDateString ? undefined : dateString, (this.isDateString(dateString as string) && !this.isISODateString(dateString as string) ? this.shortDateFormat : null)!);
	}

	getMomentInstance (): typeof moment {
		return moment;
	}

	getDateFromTimeZone (timeZone: string, date?: string, ): moment.Moment {
		return date ? moment.tz(date, timeZone) : moment.tz(timeZone);
	}

	getMoscowDateTime (date?: string): moment.Moment {
		return this.getDateFromTimeZone('Europe/Moscow', date);
	}

	hasTime (dateString: string): boolean {
		return !!dateString && dateString.indexOf && dateString.indexOf('T') !== -1;
	}

	isInputTimeString (timeString: string | null): boolean {
		return isString(timeString) && this.inputTimeRegex.test(timeString);
	}

	getTimezoneOffset (): string {
		return moment().format('Z');
	}

	isChangedDates (newValues: moment.Moment | string | [], oldValues: moment.Moment | string | []): boolean {
		let isChanged;

		if (isArray(newValues)) {
			isChanged = some(newValues, (value, index) => !this.getMoment(value).isSame(this.getMoment(oldValues[index])));
		} else {
			isChanged = !this.getMoment(newValues as string).isSame(this.getMoment(oldValues as string));
		}

		return isChanged;
	}

	getLastDaysDate (daysCount: number): moment.Moment {
		return moment().add(-daysCount, 'days');
	}

	isDayOff (date: moment.Moment): boolean {
		if (this.isWorkingDayInWeek(date)) {
			return !!find(this.nonWorkingDaysRules, (rule) => rule(date));

		} else {
			return true;
		}
	}

	isWorkingDayInWeek (date: moment.Moment): boolean {
		const dayIndex = date.day() as Int;
		return -1 < this.workingDayIndexes.indexOf(dayIndex);
	}

	getDiffAsDays (startDate: moment.Moment, endDate: moment.Moment): number {
		const minutesDiff = (endDate.unix() - startDate.unix()) / this.secondsInMinute;
		const offsetDiff = endDate.utcOffset() - startDate.utcOffset();

		return (minutesDiff + offsetDiff) / (this.minutesInHour * this.hoursInDay);
	}

	add (date: moment.Moment, value: number, period: TCommonServerDateMomentPeriod | TCommonServerDateMomentPeriodQuarter): moment.Moment {
		const isFixPeriod = -1 < this.TIME_KEYS_FOR_DST_FIX.indexOf(period);

		let oldHour;
		let newHour;
		let diff;

		if (isFixPeriod) {
			oldHour = date.hours();
			date.add(value, period);
			newHour = date.hours();

			if (oldHour !== newHour) {
				diff = this.getCountDiffHours(newHour, oldHour);

				if (0 < diff) {
					date.add(diff, 'hours');
				}
			}

			return date;
		} else {
			return date.add(value, period);
		}
	}

	isDateString (dateString: string): boolean {
		return isString(dateString) && (this.dateTestRegex.test(dateString) || this.isISODateString(dateString));
	}

	isInputDateString (dateString: string): boolean {
		return isString(dateString) && (this.inputDateTestRegex.test(dateString) || this.isISODateString(dateString));
	}

	isISODateString (dateString: string): boolean {
		return isString(dateString) && this.isoDateTestRegex.test(dateString);
	}

	// convert date (or array of dates) to UTC ISO 8601 formatted string
	toISOString (date: string | moment.Moment): string {
		return date ? this.getMoment(date).clone().toISOString() : '';
	}

	toLocalDateTimeFormatString (date: string | moment.Moment): string {
		return date ? this.getMoment(date).clone().format(this.localDateTimeFormat) : '';
	}

	toDateString (date: string | moment.Moment): TCommonServerDateFormatString | string {
		return date ? this.getMoment(date).clone().format(this.dateFormat) : '';
	}

	toDisplayDateString (date: moment.Moment | string): string {
		return date ? this.getMoment(date).clone().format(this.shortDateFormat) : '';
	}

	toDisplayTimeString (date: moment.Moment | string): string {
		return date ? this.getMoment(date).clone().format(this.displayTimeFormat) : '';
	}

	setInputSettings (): void {
		const monthsSymbol = this.commonLocaleService.instant('common.symbols.months');
		const yearsSymbol = this.commonLocaleService.instant('common.symbols.years');
		const daysSymbol = this.commonLocaleService.instant('common.symbols.days');
		const minutesSymbol = this.commonLocaleService.instant('common.symbols.minutes');
		const hoursSymbol = this.commonLocaleService.instant('common.symbols.hours');

		const mask = this.inputDateFormat
			.toLowerCase()
			.replace(/d{1,2}/i, DAYS_SYMBOL)
			.replace(/m{1,2}/i, MONTHS_SYMBOL)
			.replace(/y{1,4}/i, YEARS_SYMBOL);

		const placeholder = mask
			.replace(/d/g, daysSymbol)
			.replace(/m/g, monthsSymbol)
			.replace(/y/g, yearsSymbol);

		const maskWithoutDelimiters = mask.replace(/\W/g, '');
		this.maskDelimiter = this.inputDateFormat.replace(/\w/g, '')[0];

		this.dateTestRegex = this.dateTestRegexByYear[maskWithoutDelimiters];
		this.inputDateTestRegex = this.inputDateTestRegexByYear[maskWithoutDelimiters];
		this.inputDateMask = mask;
		this.inputDatePlaceholder = placeholder;
		this.inputTimeMask = 'h:s';
		this.inputTimePlaceholder = this.inputTimeMask.replace(/h/g, hoursSymbol + hoursSymbol).replace(/s/g, minutesSymbol + minutesSymbol);
	}

	private extendDateFormatsFromGeneralSettings (): void {
		const dateFormatSettings = this.commonGeneralSettingsService.getGeneralSettings('DateFormat');

		if (!isEmpty(dateFormatSettings)) {
			const timeFormatSettings = this.commonGeneralSettingsService.getGeneralSettings('TimeFormat');

			this.displayTimeFormat = timeFormatSettings.Format;
			this.shortDateFormat = dateFormatSettings.ShortFormat;
			this.mediumDateFormat = dateFormatSettings.MediumFormat;
			this.longDateFormat = dateFormatSettings.LongFormat;
			this.inputDateFormat = this.shortDateFormat;
			this.maskDelimiter = this.inputDateFormat.replace(/\w/g, '')[0];
			this.shortWithTime = `${this.shortDateFormat}, ${this.displayTimeFormat}`;
			this.mediumWithTime = `${this.mediumDateFormat}, ${this.displayTimeFormat}`;
			this.timeWithMedium = `${this.displayTimeFormat}, ${this.mediumDateFormat}`;
			this.amDesignator = timeFormatSettings.AmDesignator;
			this.pmDesignator = timeFormatSettings.PmDesignator;
			this.firstDay = this.commonGeneralSettingsService.getGeneralSettings('DayOfWeek').IndexNumber + 1;
			this.inputFormatWithFirstZero = -1 < this.inputDateFormat.indexOf('DD');
		} else {
			extend(this, DEFAULT_SETTINGS);
		}
	}

	private afterOrEqual (
		firstYear: number,
		firstMonth: number,
		firstDay: number,
		secondYear: number,
		secondMonth: number,
		secondDay: number,
	): boolean {
		return firstYear < secondYear || (firstYear === secondYear && (
			firstMonth < secondMonth || (firstMonth === secondMonth && (
				firstDay < secondDay || firstDay === secondDay)
			))
		);
	}

	private createNonWorkingRules (nonWorkingDays: ICommonServerDateNonWorkingDay[]): TCommonServerNonWorkingRule[] {
		return map(nonWorkingDays, (day) => {
			const startDate = this.getMoment(day.StartDate);
			const startYear = startDate.year();
			const startMonth = startDate.month();
			const startDayInMonth = startDate.date();

			if (day.EndDate) {
				const endDate = this.getMoment(day.EndDate);
				const endYear = endDate.year();
				const endMonth = endDate.month();
				const endDayInMonth = endDate.date();

				return (testDate: moment.Moment): boolean => {
					const year = testDate.year();
					const month = testDate.month();
					const dayInMonth = testDate.date();
					const afterStart = this.afterOrEqual(startYear, startMonth, startDayInMonth, year, month, dayInMonth);
					const beforeEnd = this.afterOrEqual(year, month, dayInMonth, endYear, endMonth, endDayInMonth);

					return afterStart && beforeEnd;
				};

			} else {
				return (testDate: moment.Moment): boolean => {
					const year = testDate.year();
					const month = testDate.month();
					const dayInMonth = testDate.date();

					return startYear === year && startMonth === month && startDayInMonth === dayInMonth;
				};
			}
		});
	}

	private getCountDiffHours (newHour: number, oldHour: number): number {
		const newHourInRange = 0 <= newHour && newHour < this.hoursInDay;
		const oldHourInRange = 0 <= oldHour && oldHour < this.hoursInDay;

		let leftShift;
		let rightShift;

		if (newHourInRange && oldHourInRange) {
			leftShift = 0;
			rightShift = 0;

			while (true) {
				if (this.normalizeHour(newHour + leftShift) === oldHour) {
					return leftShift;
				} else {
					leftShift -= 1;
				}
				if (this.normalizeHour(newHour + rightShift) === oldHour) {
					return rightShift;
				} else {
					rightShift += 1;
				}
			}
		} else {
			return 0;
		}
	}

	private normalizeHour (hour: number): number {
		if (this.hoursInDay <= hour) {
			return this.normalizeHour(hour - this.hoursInDay);
		} else if (hour < 0) {
			return this.normalizeHour(hour + this.hoursInDay);
		} else {
			return hour;
		}
	}
}
